import React, { useState } from 'react';
import type { ContextModalProps } from '@mantine/modals';
import { EditTripModal } from '../components/trips/EditTripModal';
import { TripEditFormModal } from '../components/trips/TripEditFormModal';
import InitialSetupContextModal from './InitialSetupContextModal';
import type { EditTripContextInnerProps } from './types';
import type { Leg } from '../types/trip';
import { isoToDateInAppTz } from '@/lib/dayjs';

// The context modal will manage its own local state mirroring what EditTripModal expects,
// then call innerProps.onSave(updatedTrip) when the user saves. Return content-only
// because ModalsProvider supplies the modal chrome.
const EditTripContextModal = ({
  context,
  id,
  innerProps,
}: ContextModalProps<EditTripContextInnerProps>) => {
  const initialTrip = innerProps?.trip;

  const handleEdit = () => {
    // Close current modal and open the edit form modal
    context.closeModal(id);
    context.openContextModal('edit-trip-form', {
      size: 'xl',
      title: 'Editar Viaje',
      innerProps: {
        trip: initialTrip,
        onSave: innerProps?.onSave,
      },
    });
  };

  return (
    <EditTripModal onClose={() => context.closeModal(id)} trip={initialTrip} onEdit={handleEdit} />
  );
};

const EditTripFormContextModal = ({
  context,
  id,
  innerProps,
}: ContextModalProps<EditTripContextInnerProps>) => {
  const initialTrip = innerProps?.trip;

  // Convert trip to individual props for TripEditFormModal
  // Use canonical helper for reading persisted values
  const [inicio, setInicio] = useState<Date | null>(
    initialTrip && 'legs' in initialTrip && initialTrip.legs && initialTrip.legs[0]
      ? isoToDateInAppTz(initialTrip.legs[0].inicio)
      : null
  );
  const [fin, setFin] = useState<Date | null>(
    initialTrip && 'legs' in initialTrip && initialTrip.legs && initialTrip.legs[0]
      ? isoToDateInAppTz(initialTrip.legs[0].fin)
      : null
  );
  const [tipo, setTipo] = useState<'Internacional' | 'Nacional'>(
    (initialTrip && 'legs' in initialTrip && initialTrip.legs[0]?.tipo) || 'Internacional'
  );
  const [region, setRegion] = useState<string | undefined>(
    initialTrip && 'legs' in initialTrip ? initialTrip.legs[0]?.region : undefined
  );
  const [error, _setError] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [legsBuffer, setLegsBuffer] = useState<Leg[]>(
    initialTrip && 'legs' in initialTrip ? initialTrip.legs : []
  );
  const [editingLegIndex, setEditingLegIndex] = useState<number | null>(null);

  const handleSave = () => {
    if (!innerProps?.onSave) {
      context.closeModal(id);
      return;
    }
    // Validate that there is at least one leg
    if (legsBuffer.length === 0) {
      _setError('Añade al menos un tramo antes de guardar');
      return;
    }
    // Build trip object
    const updatedTrip = { legs: legsBuffer };
    innerProps.onSave(updatedTrip);
    context.closeModal(id);
  };

  return (
    <TripEditFormModal
      onClose={() => context.closeModal(id)}
      inicio={inicio}
      fin={fin}
      tipo={tipo}
      region={region}
      error={error}
      showBreakdown={showBreakdown}
      legsBuffer={legsBuffer}
      editingLegIndex={editingLegIndex}
      setInicio={setInicio}
      setFin={setFin}
      setTipo={setTipo}
      setRegion={setRegion}
      setShowBreakdown={setShowBreakdown}
      setLegsBuffer={setLegsBuffer}
      setEditingLegIndex={setEditingLegIndex}
      handleSave={handleSave}
    />
  );
};

type ModalMap = {
  'edit-trip': (props: ContextModalProps<EditTripContextInnerProps>) => React.ReactElement | null;
  'edit-trip-form': (
    props: ContextModalProps<EditTripContextInnerProps>
  ) => React.ReactElement | null;
  'initial-setup': (props: ContextModalProps<{}>) => React.ReactElement | null;
};

export const modals: ModalMap = {
  'edit-trip': EditTripContextModal,
  'edit-trip-form': EditTripFormContextModal,
  'initial-setup': InitialSetupContextModal,
};

export default modals;
